/**
 * **Stream D — the people-facing paper** (Rebuild 39's People.dc.html): the sponsorship deck's
 * tier page (DRAFT · TIERS while Sponsors' roster says the year's tiers are not final — none set,
 * or one still carrying another year's amounts — and the frame reads Sponsors' own tier table),
 * the "We need chairs" poster (the areas with nobody chairing them this year, from Areas) and the
 * chair handbook's year (its dates from Year settings). A letter is signed by a named person and
 * sent by that person, never by CIDER; a field with no source prints its label and a line.
 */
import type { ReactNode } from 'react';
import { formatDate, formatMoney } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';
import { usePrinted } from './printed.js';
import { SheetPages, type SheetLine } from './sheets.js';

interface Level {
  id: string;
  name: string;
  amountCents: number;
  benefits: string[];
  kind: 'tier' | 'opportunity';
  active: boolean;
}

/** D01 · the sponsorship deck's tiers, DRAFT while the roster says the tiers are not final. */
export function SponsorDeck({ year }: { year: string }): ReactNode {
  const d = useLoad<Level[]>(`/marketing/sponsorships/levels?year=${year}`);
  const roster = useLoad<{ tiersDraft?: boolean }>(`/marketing/sponsorships?year=${year}`);
  const ready = !!d.data && (roster.data !== null || roster.error !== null);
  usePrinted('sponsor-deck', ready, { year: Number(year) });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data || !ready) return <p>Reading the levels…</p>;
  // Draft unless the roster says plainly that the tiers are final; a roster that did not answer is not that.
  const draft = roster.data?.tiersDraft !== false;
  const tiers = d.data
    .filter((l) => l.kind === 'tier' && l.active)
    .sort((a, b) => b.amountCents - a.amountCents);
  return (
    <SheetPages
      doc={{
        size: 'half',
        title: `Sponsor Apple Fest ${year}${draft ? ' · DRAFT · TIERS' : ''}`,
        sub: 'Saint Lawrence Parish · Utica · the weekend after Labor Day',
        name: 'Sponsorship deck · the tiers',
        cols: ['mid', 'fig', 'wide'],
        lines: [
          { head: draft ? 'TIERS · DRAFT UNTIL THE YEAR’S OWN ARE SET' : 'TIERS' },
          ...(tiers.length
            ? tiers.map((t) => ({
                key: t.id,
                cells: [t.name, formatMoney(t.amountCents), t.benefits.join(' · ')],
              }))
            : [{ cells: [`No tiers set for ${year} yet.`] }]),
        ],
      }}
    />
  );
}

interface Area {
  id: string;
  name: string;
  year: unknown;
  chairs: { name: string; state: string }[];
}

/** D06 · "We need chairs": the areas this year with nobody chairing them, for the vestibule. */
export function NeedChairsPoster({ year }: { year: string }): ReactNode {
  const d = useLoad<Area[]>(`/admin/areas/year?year=${year}`);
  usePrinted('need-chairs', !!d.data, { year: Number(year) });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading the areas…</p>;
  const open = d.data.filter(
    (a) => a.year !== null && !a.chairs.some((c) => c.state === 'granted' || c.state === 'active'),
  );
  return (
    <SheetPages
      doc={{
        size: 'poster',
        title: 'Apple Fest needs chairs',
        sub: `For ${year} · the weekend after Labor Day`,
        name: 'Vestibule poster · reprint when an area fills',
        lead: (
          <>
            <p className="pr-big">
              {String(open.length)} {open.length === 1 ? 'area' : 'areas'}
            </p>
            <p className="pr-sub">need a chair this year</p>
          </>
        ),
        lines: [
          { head: 'OPEN' },
          ...(open.length
            ? open.map((a) => ({ key: a.id, cells: [a.name] }))
            : [{ cells: ['Every area has a chair. Thank you.'] }]),
          { head: 'WHAT A CHAIR DOES' },
          {
            cells: [
              'One weekend. A one-page plan. A phone app that tells you who is here. A chair beside you who has done it.',
            ],
          },
          { head: 'TALK TO' },
          { cells: [null] },
        ],
      }}
    />
  );
}

interface YearSettings {
  setting: {
    budgetMeetingOn?: string | null;
    seasonOpenOn?: string | null;
    setupWeekStart?: string | null;
    festivalFriday?: string | null;
    festivalSunday?: string | null;
  } | null;
}

/** D02 · the chair handbook, "A year in a weekend": the year's dates from Year settings. */
export function ChairHandbook({ year }: { year: string }): ReactNode {
  const d = useLoad<YearSettings>(`/admin/year-settings/${year}`);
  usePrinted('chair-handbook', !!d.data, { year: Number(year) });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading the year…</p>;
  const s = d.data.setting;
  const on = (x: string | null | undefined): string | null =>
    x ? formatDate(`${x}T16:00:00Z`) : null;
  const lines: SheetLine[] = [
    { head: 'THE YEAR' },
    { cells: ['The budget meeting · your lines are set', on(s?.budgetMeetingOn)] },
    { cells: ['Season open · the Asks close and the budget locks', on(s?.seasonOpenOn)] },
    { cells: ['Setup week begins', on(s?.setupWeekStart)] },
    {
      cells: [
        'The festival',
        s?.festivalFriday && s.festivalSunday
          ? `${on(s.festivalFriday) ?? ''} – ${on(s.festivalSunday) ?? ''}`
          : null,
      ],
    },
    { cells: ['How did it go? · your notes feed next year', 'the week after'] },
    { head: 'THE THREE THINGS' },
    { cells: ['Ask for more', 'I spent', 'Open a PO'] },
    { cells: ['a budget change, in your words', 'a receipt against a line', 'they’ll bill us'] },
    { head: 'FIRST YEAR? START HERE' },
    { cells: ['Sign in to the chair app'], tick: true },
    { cells: ['Read your area one-pager'], tick: true },
    { cells: ['Meet the finance director'], tick: true },
    { cells: ['Walk your area with last year’s chair'], tick: true },
  ];
  return (
    <SheetPages
      doc={{
        size: 'letter',
        title: 'A year in a weekend',
        sub: `The chair handbook · Apple Fest ${year}`,
        name: 'Chair handbook',
        lead: (
          <p className="pr-sub">
            Thank you for running an area. This is everything you need and nothing you don’t.
          </p>
        ),
        cols: ['wide', 'mid', 'mid'],
        lines,
      }}
    />
  );
}
